<template>
  <PageView :main-title="$t(`DocumentsPage.title`)" :no-sub-title-expected="true">
    <div class="year-navigation">
      <b-button
        class="button is-primary"
        :disabled="selectedAnneeScolaire === anneesScolaires[anneesScolaires.length - 1]"
        outlined
        @click="previousYear"
      >
        {{ $t("graePage.details.projets.precedent") }}
      </b-button>
      <b-select v-model="selectedAnneeScolaire" @update:model-value="goToYear">
        <template v-for="option in anneesScolaires" :key="option">
          <option :value="option">
            {{ option }}
          </option>
        </template>
      </b-select>
      <b-button
        :disabled="selectedAnneeScolaire === anneesScolaires[0]"
        class="button is-primary"
        outlined
        @click="nextYear"
      >
        {{ $t("graePage.details.projets.suivant") }}
      </b-button>
    </div>
    <ContentView class="ImportDiplomeView">
      <div class="Import-Diplome-buttons-container">
        <b-button
          :disabled="isObservateurNational"
          class="button is-primary"
          outlined
          @click="importDiplome"
        >
          {{ $t("DocumentsPage.importer_un_diplome") }}
        </b-button>
      </div>
      <b-table
        :ref="tableRef"
        :striped="config.isStriped"
        :hoverable="config.isHoverable"
        :debounce-search="config.searchDebounce"
        :aria-next-label="config.nextLabel()"
        :aria-previous-label="config.previousLabel()"
        :aria-page-label="config.pageLabel()"
        :aria-current-label="config.currentPageLabel()"
        :per-page="config.perPage"
        :sticky-header="config.stickyHeader"
        :current-page="currentPagedSortedParameter.pageNumber"
        :pagination-position="config.paginationPosition"
        paginated
        backend-pagination
        :sort-icon="config.sortIcon"
        :sort-icon-size="config.sortIconSize"
        backend-sorting
        sort-multiple
        :sort-multiple-data="buefySortingPriority"
        :total="total"
        :data="documents"
        :loading="isLoading"
        @sort="onSort"
        @sorting-priority-removed="sortingPriorityRemoved"
        @page-change="onPageChange"
      >
        <b-table-column
          v-for="(column, i) in columns"
          :key="i"
          v-bind="column"
          :visible="column.visible"
        >
          <template #default="props">
            <div class="clickable" @click="downloadDoc(props.row)">
              <div v-if="column.field === 'annees'">
                {{ props.row["anneescolaire"] }}
              </div>
              <div v-if="column.field === 'typeDoc'">
                {{ props.row["type"] }}
              </div>
              <div v-if="column.field === 'name'">
                {{ props.row["file"] }}
              </div>
              <div v-if="column.field === 'action'">
                <b-button
                  v-if="isAnimateur && (isNational || isAnimateur)"
                  type="is-danger is-outlined"
                  icon-left="trash"
                  :title="$t('delete')"
                  @click="deleteDocument($event, props.row)"
                />
              </div>
              <div v-if="!column.custom" class="clickable">
                <div>{{ props.row[column.field] }}</div>
              </div>
            </div>
          </template>
        </b-table-column>
      </b-table>
      <DocumentsModalView
        :open="openDocumentModal"
        :data="documents"
        @close="openDocumentModal = false"
        @close-refresh="submitDocumentEmit"
      />
    </ContentView>
  </PageView>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import PageView from "@/views/commons/PageView.vue"
import ContentView from "@/views/commons/ContentView.vue"
import {
  DocumentsDTO,
  FilterDTO,
  PaginationOrder,
  PaginationParameter,
  PaginationResult,
  ParametrageDTO,
} from "@/model/bean/GeneratedDTOs"
import { LoginService } from "@/services/rest/login/LoginService"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import DocumentsModalView from "@/pages/document/DocumentsModalView.vue"
import { DocumentsService } from "@/services/rest/documents/DocumentsService"
import Column from "@/views/tables/Column"
import { Filters } from "@/model/bean/Filters"
import { GraeService } from "@/services/rest/grae/GraeService"
import { useTableView } from "@/hooks/useTableView"
import { SortOrder } from "@/model/SortOrder"
import { BButton, BTable, BTableColumn, BSelect } from "buefy"
import { UsersService } from "@/services/rest/users/UsersService"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"

export default defineComponent({
  name: "DocumentsPage",
  components: {
    DocumentsModalView,
    PageView,
    ContentView,
    BButton,
    BSelect,
    BTable,
    BTableColumn,
  },
  data() {
    return {
      loginService: LoginService.INSTANCE,
      usersService: UsersService.INSTANCE,
      documentService: DocumentsService.INSTANCE,
      graeService: GraeService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      selectedAnneeScolaire: "",
      anneesScolaires: [] as Array<string>,
      isAnimateur: false,
      isNational: false,
      documents: [] as DocumentsDTO[],
      openDocumentModal: false,
      isObservateurNational: false,
      parametrage: new ParametrageDTO(),
      tableView: null as any,
    }
  },
  computed: {
    // Propriétés mappées depuis tableView.state
    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },
    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },
    currentPagedSortedParameter(): PaginationParameter {
      return this.tableView?.state.currentPagedSortedParameter
    },
    activeFilters(): Array<FilterDTO> {
      return this.tableView?.state.activeFilters || []
    },
    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },
    total(): number {
      return this.tableView?.state.total || 0
    },
    config(): any {
      return this.tableView?.state.config
    },
    tableRef(): string {
      return this.tableView?.state.tableRef || "tableRef"
    },
  },
  watch: {
    openDocumentModal(): void {
      this.refreshTable()
    },
  },
  created(): void {
    // Initialisation du hook avec binding correct
    this.tableView = useTableView({
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      tableRef: "tableRef",
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
      reloadDataWhenMounted: false,
    })

    const loggedUser = this.loginService.getLoggedUser()

    if (!loggedUser) {
      return
    }

    this.isNational = this.usersService.hasAnyProfile(loggedUser, [
      PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR,
      PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
      PROFIL_UTILISATEUR.OBSERVATEUR_NATIONAL,
    ])

    this.isAnimateur =
      this.isNational ||
      this.usersService.hasAnyProfile(loggedUser, [PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL])

    this.isObservateurNational = this.usersService.hasAnyProfile(loggedUser, [
      PROFIL_UTILISATEUR.OBSERVATEUR_NATIONAL,
    ])

    this.tableView.refreshInitialColumns()
  },
  mounted(): void {
    this.tableView?.initMounted()
    this.loadAnneesScolaires()
  },
  methods: {
    // Méthodes de tableView
    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView.sortingPriorityRemoved(field)
      }
    },
    updateTotal(total: number): void {
      this.tableView?.updateTotal(total)
    },
    // Méthodes conservées de configuration
    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },
    getDefaultSort(): Array<PaginationOrder> {
      return new Array<PaginationOrder>()
    },
    async reloadData(pagination?: PaginationParameter, filters?: Array<FilterDTO>): Promise<void> {
      try {
        const paginatedDocumentsList: PaginationResult<DocumentsDTO> =
          await this.documentService.getAllDocuments(
            pagination || this.currentPagedSortedParameter,
            filters || this.activeFilters,
          )
        this.documents = paginatedDocumentsList.elements
        this.updateTotal(paginatedDocumentsList.count)
      } catch (e: any) {
        // Silent catch
      }
    },
    refreshTable(): void {
      this.tableView?.reload()
    },
    importDiplome(): void {
      this.openDocumentModal = true
    },
    async submitDocumentEmit(document: DocumentsDTO): Promise<void> {
      this.openDocumentModal = false
      try {
        await this.documentService.createDocument(document)
        this.infoReportingService.success(this.$t("DocumentsPage.success-upload").toString())
      } catch (e: any) {
        console.error("Erreur lors de l'enregistrement du document", e)
        this.infoReportingService.error(
          this.$t("DocumentsPage.error-upload").toString(),
          e as Error,
        )
      }
      this.tableView.reload()
    },
    deleteDocument(event: Event, docToDelete: DocumentsDTO): void {
      event.stopPropagation()
      this.infoReportingService.dialog(
        this.$t("graePage.confirmation").toString(),
        this.$t("DocumentsPage.confirm-delete-document").toString(),
        this.$t("DocumentsPage.success-delete").toString(),
        () => this.doDeleteDocument(docToDelete),
        "is-danger",
      )
    },
    async doDeleteDocument(docToDelete: DocumentsDTO): Promise<void> {
      try {
        await this.documentService.deleteDocument(docToDelete)
      } catch (e: any) {
        this.infoReportingService.error(
          this.$t("DocumentsPage.error-delete").toString(),
          e as Error,
        )
      }
      this.tableView?.reload()
    },
    async downloadDoc(docToGet: DocumentsDTO): Promise<void> {
      window.location.href = "./api/v2/document/" + docToGet.id
    },
    getInitialColumns(): Array<Column> {
      const initialColumns: Array<Column> = [
        {
          field: "annees",
          label: this.$t("DocumentsPage.anneescolaire").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "typeDoc",
          label: this.$t("DocumentsPage.type").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "name",
          label: this.$t("DocumentsPage.name").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
      ]

      if (this.isAnimateur && !this.isObservateurNational) {
        initialColumns.push({
          field: "action",
          label: this.$t("projetsListPage.actions").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        })
      }
      return initialColumns
    },
    previousYear(): void {
      const thisYearIndex = this.anneesScolaires.indexOf(this.selectedAnneeScolaire)
      const previousYear = this.anneesScolaires[thisYearIndex + 1]

      this.clearAnneeScolaireFilter()

      this.activeFilters.push(
        Filters.asFilter({ property: "anneeScolaire", values: [previousYear] }),
      )
      this.tableView?.reload()
      this.selectedAnneeScolaire = previousYear
    },
    nextYear(): void {
      const thisYearIndex = this.anneesScolaires.indexOf(this.selectedAnneeScolaire)
      const nextYear = this.anneesScolaires[thisYearIndex - 1]

      this.clearAnneeScolaireFilter()

      this.activeFilters.push(Filters.asFilter({ property: "anneeScolaire", values: [nextYear] }))
      this.tableView?.reload()
      this.selectedAnneeScolaire = nextYear
    },
    goToYear(newValue?: string): void {
      if (newValue) {
        this.selectedAnneeScolaire = newValue
      }
      this.$nextTick(() => {
        this.clearAnneeScolaireFilter()

        this.activeFilters.push(
          Filters.asFilter({ property: "anneeScolaire", values: [this.selectedAnneeScolaire] }),
        )

        this.tableView?.reload()
      })
    },
    async loadAnneesScolaires(): Promise<void> {
      this.anneesScolaires = await this.graeService.getAnneesScolaires()
      this.selectedAnneeScolaire ||= this.anneesScolaires[0]
      this.goToYear()
    },
    clearAnneeScolaireFilter(): void {
      this.tableView?.setActiveFilters(
        this.activeFilters.filter((item) => item.property !== "anneeScolaire"),
      )
    },
  },
})
</script>

<style lang="scss">
.Import-Diplome-buttons-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

#app {
  .ImportDiplomeView {
    .b-table .table-wrapper.has-sticky-header {
      min-height: calc(100vh - 80px - 104px - 60px - 44px - 44px - 80px);
    }
  }
}
</style>
